import { useEffect, useMemo, useRef } from "react";
import mermaid, { type MermaidConfig } from "mermaid";

type MermaidDiagramProps = {
  definition: string;
  config?: MermaidConfig;
};

export function MermaidDiagram({ definition, config }: MermaidDiagramProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderId = useMemo(
    () => `mermaid-${Math.random().toString(36).slice(2)}`,
    [definition, config]
  );

  useEffect(() => {
    let cancelled = false;

    async function renderDiagram() {
      try {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "loose",
          theme: "default",
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: "basis",
            ...config?.flowchart
          },
          ...config
        });

        const { svg } = await mermaid.render(renderId, definition);
        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (error) {
        if (!cancelled && containerRef.current) {
          const message =
            error instanceof Error ? error.message : "Unable to render Mermaid diagram";
          containerRef.current.innerHTML = `<pre>${message}</pre>`;
        }
      }
    }

    renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [definition, renderId, config]);

  return <div ref={containerRef} className="mermaid-diagram" />;
}
